<template>
  <div
    class="exchange-swap-form p-16 p-32--l bor--radius-20 bg--color-01 sha--03"
  >
    <ui-simple-tab
      :selected-button="selectedTab"
      :buttons="tabs"
      size="xl"
      class="exchange-swap-form__tabs mb-10"
      @click="
        btn => {
          tabHandle(btn.id)
        }
      "
    />
    <form
      class="exchange-swap-form__container"
      autocomplete="off"
      @submit.prevent="onSubmit"
    >
      <exchange-form-side-container
        class="exchange-swap-form__form-side-container mb-16 mb-20--l"
        without-top-space
      >
        <template #content>
          <div
            v-if="isLoggedIn"
            class="exchange-swap-form__label flx txt--12px txt--height-14px txt--color-03 mb-4"
          >
            {{ $t('exchange.balance') }}:
            <span
              v-if="currencySell"
              class="exchange-swap-form__label-value txt--weight-500--l ml-4"
            >
              {{ currencySell.tradeAvailable | noExponents }}
              {{ currencySell.ticker }}
            </span>
            <span v-else>-</span>
          </div>
          <div class="exchange-swap-form__fields">
            <div class="input">
              <div class="vertical-line" />
              <exc-input
                v-model="$v.valueSell.$model"
                :field="$v.valueSell"
                :optional-errors="{
                  ...optionalErrors,
                  ...cryptoFieldErrors,
                }"
                type="number"
                step="0.00000001"
                :placeholder="inputLabels.top"
                min="0"
                autocomplete="off"
                size="s"
                without-border
                label-disabled
                :disabled="currencyRateFetching"
                class="exchange-swap-form__input"
                @input="onInputSellValue"
              />
            </div>
            <exchange-ticker-dropdown-new
              v-model="currencySell"
              :options="tickerOptionsSell"
              :loading="tickerOptionsSell === null"
              size="s"
              class="exchange-swap-form__select-currency"
              balance-key="tradeAvailable"
            />
          </div>
        </template>
      </exchange-form-side-container>

      <exchange-form-side-container
        class="exchange-swap-form__form-side-container exchange-swap-form__side-with-svg"
        without-top-space
      >
        <template #content>
          <div
            v-if="currencySell && currencyBuy"
            class="exchange-swap-form__label flx txt--12px txt--height-14px txt--color-03 mb-4"
          >
            <span class="">
              {{ currencyMessage }}
            </span>
          </div>
          <div
            class="exchange-swap-form__fields bg--theme-001 bor--all bor--transp-01 p-20"
          >
            <div class="input">
              <div class="vertical-line" />
              <exc-input
                v-model="$v.valueBuy.$model"
                :field="$v.valueBuy"
                :optional-errors="{
                  ...optionalErrors,
                  ...fiatFieldErrors,
                }"
                type="number"
                :placeholder="inputLabels.bottom"
                step="0.00000001"
                min="0"
                autocomplete="off"
                size="s"
                without-border
                label-disabled
                :disabled="currencyRateFetching"
                class="exchange-swap-form__input"
                @input="onInputBuyValue"
              />
            </div>
            <exchange-ticker-dropdown-new
              v-model="currencyBuy"
              :options="tickerOptionsBuy"
              :loading="tickerOptionsBuy == null"
              size="s"
              balance-key="tradeAvailable"
              hide-balance
              class="exchange-swap-form__select-currency"
            />
          </div>
        </template>
      </exchange-form-side-container>

      <div class="exchange-swap-form__submit-container mt-24">
        <div class="exchange-swap-form__button-container">
          <div v-if="!isLoggedIn">
            <exc-button
              class="exchange-swap-form__submit"
              size="m"
              full-width
              :to="localePath('/login')"
            >
              {{ $t('buySellCrypto.login') }}
            </exc-button>
          </div>
          <exc-button
            v-else
            :disabled="$v.$invalid"
            :loading="submitting"
            full-width
            class="exchange-swap-form__submit"
            size="m"
            type="submit"
          >
            {{ btnText }}
          </exc-button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { notifySuccess } from '@/lib/bus'
import {
  valueSetterCutter,
  createAndSubmitPaymentForm,
  parseNumber,
  noExponents,
} from '@/lib/utils'
import { serverFormMixin } from '@/mixins/serverFormMixin'
import { ExcButton, ExcInput } from '@/components/common'
import Decimal from 'decimal.js-light'
import { validationMixin } from 'vuelidate'
import { required, minValue } from 'vuelidate/lib/validators'
import { mapGetters, mapState, mapActions } from 'vuex'

const tabsList = {
  buy: 'Buy',
  sell: 'Sell',
}

export default {
  name: 'ExchangeLatamexForm',
  components: {
    ExcInput,
    ExcButton,
    ExchangeFormSideContainer: () =>
      import('@/components/exchange/ExchangeFormSideContainer.vue'),
    ExchangeTickerDropdownNew: () =>
      import('@/components/exchange/ExchangeTickerDropdownNew.vue'),
    UiSimpleTab: () =>
      import(
        /* webpackMode: "eager" */ '@/components/ui-kit/tabs/UiSimpleTab.vue'
      ),
  },
  filters: { noExponents },
  mixins: [validationMixin, serverFormMixin],
  data () {
    return {
      tabsList,

      currencyRateFetching: false,

      selectedTab: tabsList.buy,
      // BUY
      valueBuy: null,
      currencyBuy: null,

      // SELL
      valueSell: null,
      currencySell: null,

      submitting: false,
    }
  },

  computed: {
    ...mapGetters({
      balanceByTicker: 'wallet/balanceByTicker',
    }),

    ...mapState({
      isLoggedIn: state => state.auth.loggedIn,
      latamexTickerList: state => state.latamexExchange.exhangeData,
      latamexCurrencyRates: state => state.latamexExchange.latamexCurrencyRates,
    }),

    tabs () {
      return [
        {
          id: tabsList.buy,
          text: this.$t('buySellCrypto.buyTab'),
        },
        {
          id: tabsList.sell,
          text: this.$t('buySellCrypto.sellTab'),
          activeColor: 'txt--red',
        },
      ]
    },

    isBtnFullWidth () {
      if (!process.client) return
      return this.$mq.resize && this.$mq.below(768)
    },

    inputLabels () {
      const buyLabel = this.$t('buySellCrypto.buyLabel') // wtf === I want to sell
      const sellLabel = this.$t('buySellCrypto.sellLabel') // wtf === I will receive

      return {
        top: this.selectedTab === tabsList.sell ? buyLabel : sellLabel,
        bottom: this.selectedTab === tabsList.sell ? sellLabel : buyLabel,
      }
    },

    btnText () {
      const buyBtn = this.$t('buySellCrypto.buyBtn', {
        ticker: this.currencySell.ticker,
      })
      const sellBtn = this.$t('buySellCrypto.sellBtn', {
        ticker: this.currencySell.ticker,
      })

      return this.selectedTab === tabsList.sell ? sellBtn : buyBtn
    },

    optionalErrors () {
      return {
        greaterThanZero: this.$t('validators.greaterThanZero'),
        greaterThanBalance: this.$t('validators.greaterThanBalance'),
        required: this.$t('validators.required'),
      }
    },

    cryptoFieldErrors () {
      return this.currencyRateForPair
        ? {
            minValue: this.$t('validators.minValue', {
              min: this.currencyRateForPair.cryptoMin,
            }),
          }
        : {}
    },

    fiatFieldErrors () {
      return this.currencyRateForPair
        ? {
            minValue: this.$t('validators.minValue', {
              min: this.currencyRateForPair.fiatMin,
            }),
          }
        : {}
    },

    // sell dropdown menu (from)
    tickerOptionsSell () {
      if (!Object.keys(this.latamexTickerList).length) return []

      const cryptoTickers = (this.selectedTab === tabsList.buy
        ? this.latamexTickerList.buy.crypto
        : this.latamexTickerList.sell.crypto
      ).map((el) => {
        return {
          ...el,
          latamexId: el.id,
        }
      })

      if (this.isLoggedIn) {
        return cryptoTickers.map((el) => {
          return {
            ...el,
            ...this.balanceByTicker(el.ticker),
          }
        })
      }

      return cryptoTickers
    },

    // buy dropdown menu (to)
    tickerOptionsBuy () {
      if (!Object.keys(this.latamexTickerList).length) return []

      return this.selectedTab === tabsList.buy
        ? this.latamexTickerList.buy.fiat
        : this.latamexTickerList.sell.fiat
    },

    selectedPair () {
      if (!this.currencyBuy || !this.currencySell) return {}
      return {
        fiatId: this.currencyBuy.id,
        cryptoId: this.currencySell.latamexId,
        pairTickers: `${this.currencySell.ticker}_${this.currencyBuy.ticker}`.toLowerCase(),
      }
    },

    requestBody () {
      const val = {
        amount:
          this.selectedTab === tabsList.buy
            ? valueSetterCutter(this.valueBuy)
            : valueSetterCutter(this.valueSell),
        fiat_id: this.selectedPair?.fiatId,
        crypto_id: this.selectedPair?.cryptoId,
        side: this.selectedTab,
      }

      return val
    },

    currencyRateForPair () {
      const isCurrencyDataExist = !!(
        this.latamexCurrencyRates[this.selectedPair.pairTickers] &&
        this.latamexCurrencyRates[this.selectedPair.pairTickers][
          this.selectedTab
        ]
      )

      return isCurrencyDataExist
        ? this.latamexCurrencyRates[this.selectedPair.pairTickers][
          this.selectedTab
        ]
        : null
    },

    currencyMessage () {
      const currencyFiatRate = this.currencyRateForPair
        ? this.currencyRateForPair.quote
        : '-'

      return `1 ${this.currencySell.ticker} ≈ ${this.currencyBuy.ticker} ${currencyFiatRate}`
    },
  },

  watch: {
    async selectedPair (v) {
      // if pair selected correctly and we don't already have exhange rate
      this.valueSell = null
      this.valueBuy = null
      this.$v.$reset()

      if (v.cryptoId && v.fiatId && !this.currencyRateForPair) {
        this.currencyRateFetching = true

        await this.getLatamexCurrencyRate({
          crypto_id: v.cryptoId,
          fiat_id: v.fiatId,
          side: this.selectedTab,
        })

        this.currencyRateFetching = false
      }
    },
    tickerOptionsSell: {
      immediate: true,
      async handler (newValue) {
        if (newValue && newValue.length) {
          this.currencySell =
            newValue.find(item => item.ticker === 'BTC') || newValue[0]
        }
      },
    },
    tickerOptionsBuy: {
      immediate: true,
      handler (newValue) {
        if (newValue && newValue.length) {
          this.currencyBuy =
            newValue.find(item => item.ticker === 'BRL') || newValue[0]
        }
      },
    },
  },

  methods: {
    ...mapActions({
      updateWalletBalances: 'wallet/updateWalletBalances',
      getLatamexCurrencyRate: 'latamexExchange/getLatamexCurrencyRate',
    }),
    tabHandle (tab) {
      if (this.selectedTab === tab) return

      this.selectedTab = tab || tabsList.sell

      this.valueSell = null
      this.valueBuy = null
      this.currencyBuy = null
      this.$v.$reset()
    },
    onInputSellValue (v) {
      if (v) {
        if (this.currencyRateForPair) {
          const value = parseNumber(
            new Decimal(v).mul(this.currencyRateForPair.quote).toString(),
          )

          this.valueBuy = value
        }
      } else {
        this.valueBuy = ''
      }

      this.$v.valueBuy.$touch()
    },
    onInputBuyValue (v) {
      if (v) {
        if (this.currencyRateForPair) {
          const value = parseNumber(
            new Decimal(v).div(this.currencyRateForPair.quote).toString(),
          )

          this.valueSell = value
        }
      } else {
        this.valueSell = ''
      }

      this.$v.valueSell.$touch()
    },
    async onSubmit () {
      this.submitting = true
      const { response, errors } = await this.$api.sendLatamexFormRequest({
        data: this.requestBody,
      })
      this.submitting = false

      if (errors) {
        this.mix_setServerErrors(errors)
        // Error popup is shown according to bootstrap.js
        // this.mix_notifyServerErrors(errors)

        if (errors) {
          this.mix_notifyServerErrors(errors, {
            customTitle: this.$t('exchange.notification.title'),
          })
        }
        return
      }

      createAndSubmitPaymentForm(response, 'POST')

      await this.updateWalletBalances()

      notifySuccess({
        title: this.$t('exchange.notification.title'),
        text: this.$t('exchange.notification.success'),
      })
    },
  },

  validations () {
    return this.mix_validationsServerWrapper({
      valueBuy: {
        required,
        greaterThanZero: v => new Decimal(v || 0).greaterThan(0),
        ...(this.currencyRateForPair && {
          minValue: minValue(this.currencyRateForPair.fiatMin),
        }),
      },
      currencyBuy: {
        required,
      },
      valueSell: {
        required,
        greaterThanBalance: (v) => {
          if (!this.isLoggedIn || this.selectedTab === tabsList.buy) return true
          const ticker = this.currencySell && this.currencySell.ticker
          if (!ticker) return true
          const bal = this.balanceByTicker(ticker).tradeAvailable
          return new Decimal(v || 0).lessThanOrEqualTo(bal)
        },
        greaterThanZero: v => new Decimal(v || 0).greaterThan(0),
        ...(this.currencyRateForPair && {
          minValue: minValue(this.currencyRateForPair.cryptoMin),
        }),
      },
      currencySell: {
        required,
      },
    })
  },
}
</script>

<style lang="scss" scoped>
.exchange-swap-form {
  z-index: 12;

  &__table-container {
    display: grid;
    grid-template-rows: 1fr min-content 1fr;
    gap: toRem(17px);

    @include bp(l) {
      gap: toRem(20px);
    }
  }

  &__fields {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: repeat(2, 1fr);
    background: color('color-01');
    border-radius: toRem(22px);
    border: 1px solid color('transp-02');
    box-sizing: border-box;
    padding: toRem(14px);
    position: relative;

    @include bp(m) {
      padding: toRem(0x) toRem(22px);
      grid-template-columns: repeat(2, 1fr);
      grid-template-rows: 1fr;
    }
  }

  &__label {
    @include bp(l) {
      justify-content: end;
    }
  }

  &__label-value {
    @include bp(l) {
      color: color('color-04');
    }
  }

  &__submit-container {
    border-bottom-left-radius: toRem(20px);
    border-bottom-right-radius: toRem(20px);
  }
}

.input {
  padding: 0;
  border-top: 0;
}

.vertical-line {
  display: none;
  @include bp(m) {
    display: block;
    border-left: 1px solid color('transp-02');
    height: toRem(20px);
    position: absolute;
    left: 50%;
    top: toRem(12px);
  }
}

::v-deep .exc-base-dropdown__container {
  @include bp(0, m) {
    border-top: 1px solid color('transp-03');
    padding-left: toRem(0);
    padding-right: toRem(0);
  }
}

::v-deep {
  .exc-input-label-error-wrapper__info {
    bottom: toRem(-6px);
    top: initial;

    @include bp(m) {
      top: calc(100% + 2px);
    }
  }

  .exc-base-input {
    border: none !important;
    border-radius: 0 !important;
    padding: 0 !important;
  }

  .exc-base-dropdown__open-icon {
    padding-right: 0 !important;
  }

  .exc-base-dropdown__options-list {
    top: toRem(16px) !important;
  }

  .exc-base-dropdown__form-control {
    padding-left: 0;

    @include bp(m) {
      padding-left: toRem(10px);
    }
  }

  .exc-input-label-error-wrapper__content {
    margin-bottom: 0;
  }
}
</style>
